import {useState, useEffect, useRef, useReducer} from "react";
import {useTranslation} from "react-i18next";
import {useParams} from "react-router-dom";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {useNavigate} from "react-router-dom";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";

import TabIdentify from "./TabIdentify";
import TabFilters from "./TabFilters";
import TabCriteria from "./TabCriteria";
import TabPreviewOverflow from "./TabPreviewOverflow";
import NavBreadcrum from "components/NavBreadcrum";
import DynamicTemplateModel from "site-apps/distribution/dynamic/DynamicTemplateModel";
import {initialStepsState, stepsReducer, useStepsTabs} from "./stepsState";
import DurationDisplay from "components/datestimes/DurationDisplay";
import navs from "../navs";
import axiApi from "ApiCaller";
import {SwalAlertClass} from "core/swalClass";
import {PointsObjectModels} from "./ModelsDynamic";

const IconographyModel = {
    name: "",
    icon: <FontAwesomeIcon icon="question" size="3x" />,
    download: "",
};

const FormTemplate = () => {
    const navigate = useNavigate();
    const {t: _Tr} = useTranslation("routes");
    const title = _Tr("labels.route-dynamic");
    const {id} = useParams();
    const Alert = new SwalAlertClass({confirmButtonText: "Aceptar"});
    const [formData, setFormData] = useState({...DynamicTemplateModel});
    const [programmings, setProgrammings] = useState([]);
    const bread = [
        {name: _Tr("labels.route-management"), url: "/route-management/", current: false},
        {name: _Tr("labels.routes-dynamic"), url: "/route-management/routes-dynamic/", current: false},
        {name: "Plantilla", current: true},
    ];
    const [keyTab, setKeyTab] = useState("identify");
    const [steps, dispatchSteps] = useReducer(stepsReducer, initialStepsState);
    const stepsTabs = useStepsTabs(dispatchSteps, steps);
    const [editingState, setEditingState] = useState({
        isNew: id === "add",
        identify: false,
        filters: false,
        criteria: false,
        preview: false,
    });

    const editing = {
        data: editingState,
        setter: setEditingState,
        set: function (name, value) {
            this.setter({...this.data, [name]: value});
        },
    };

    const dynamicAssets = useRef([]);
    const [assets, setAssets] = useState([]);
    const [iconograph, setIconograph] = useState({...IconographyModel});
    const pointsModels = useRef({...PointsObjectModels});

    useEffect(() => {
        document.title = title;
        setFormData({...DynamicTemplateModel});
        if (id !== "add") {
            axiApi.get(`/api/distribution/dynamic/route/${id}/`).then((resp) => {
                if (resp.data.status) {
                    let data = resp.data.item;
                    data.assets = resp.data.assets;
                    data.params.duration = DurationDisplay({seconds: data.params.duration, format: "HH:mm"});
                    data.travel_time = DynamicTemplateModel.travel_time;
                    data.programming = data.programming ?? "";
                    setFormData({...data});
                    setProgrammings(resp.data.programmings || []);
                    if (data.filters.waste_type && data.filters.download_type) {
                        stepsTabs.setFiltersDynamic(true);
                        stepsTabs.enableTab("filters");
                    }
                    editing.set("identify", true);
                } else {
                    Alert.error("Carga de datos.", "No se ha podido cargar los datos de la ruta.");
                }
            });
        } else {
            // alta: solo las programaciones del panel para el selector
            axiApi.get(`/api/distribution/dynamic/programmings/`).then((resp) => {
                if (resp.data.status) setProgrammings(resp.data.programmings || []);
            });
        }
    }, []);

    const onSummit = (e) => {
        const dataSend = {...formData};
        // Criterios de los contenedores evaluados (pestaña Criterios) y, para no borrarlos, los personalizados
        // guardados de los que ahora no se evalúan (no activos). El backend solo guarda los distintos de la plantilla.
        const shown = new Set(dynamicAssets.current.map((_dyn) => _dyn.asset_id));
        const kept = (formData.assets || []).filter((_dyn) => !shown.has(_dyn.asset_id));
        dataSend.dynamic_assets = [...dynamicAssets.current, ...kept].map((_dyn) => ({
            id: _dyn.asset_id,
            force: _dyn.force,
            days: _dyn.days,
            c1: _dyn.c1,
            c2: _dyn.c2,
            c3: _dyn.c3,
        }));
        dataSend.filters.groups = dataSend.filters.groups.map((_group) => _group.id);
        dataSend.filters.islands = dataSend.filters.islands.map((_island) => _island.id);
        dataSend.filters.routes = dataSend.filters.routes.map((_route) => _route.id);
        dataSend.filters.zones = dataSend.filters.zones.map((_zone) => _zone.id);
        dataSend.filters.uuids = dataSend.filters.uuids.map((_uuid) => _uuid.id);
        axiApi.post(`/api/distribution/dynamic/route/`, dataSend).then((resp) => {
            if (resp.data.status) {
                navigate("/route-management/routes-dynamic/");
            } else {
                Alert.error("Error al guardar", resp.data.message);
            }
        });
    };

    return (
        <>
            <NavBreadcrum navs={navs} bread={bread} />
            <div className="col-12 preview-canvas offcanvas-header mt-1">
                <div className="preview-icon">
                    <FontAwesomeIcon icon="route" />
                </div>

                <div className="preview-title ps-4">
                    <h5>Plantilla Dinamica</h5>
                    {formData.name && (
                        <h6>
                            <strong>{formData.name}</strong>
                        </h6>
                    )}
                </div>
                <div className="ms-auto text-secondary pe-3">
                    <div className="text-center">{iconograph.name}</div>
                    <div className="text-center">{iconograph.icon}</div>
                    <div className="text-center">{iconograph.download}</div>
                </div>
            </div>
            <div className="offcanvas-body">
                <Tabs activeKey={keyTab} id="form-template-tabs" onSelect={(k) => setKeyTab(k)} className="mb-3" justify>
                    <Tab eventKey="identify" title="Identificación">
                        <TabIdentify
                            formData={formData}
                            setFormData={setFormData}
                            dynamicAssets={dynamicAssets}
                            steps={steps}
                            stepsTabs={stepsTabs}
                            setKeyTab={setKeyTab}
                            pointsModels={pointsModels}
                            editing={editing}
                        />
                    </Tab>
                    <Tab eventKey="filters" title="Filtros" disabled={!steps.filters}>
                        <TabFilters
                            formData={formData}
                            setFormData={setFormData}
                            dynamicAssets={dynamicAssets}
                            assets={assets}
                            setAssets={setAssets}
                            setKeyTab={setKeyTab}
                            steps={steps}
                            stepsTabs={stepsTabs}
                            iconograph={iconograph}
                            setIconograph={setIconograph}
                            editing={editing}
                        />
                    </Tab>
                    <Tab eventKey="criteria" title="Criterios" disabled={!steps.criteria}>
                        <TabCriteria
                            dynamicAssets={dynamicAssets}
                            steps={steps}
                            stepsTabs={stepsTabs}
                            setKeyTab={setKeyTab}
                            editing={editing}
                        />
                    </Tab>
                    <Tab eventKey="preview" title="Vista Previa" disabled={!steps.preview}>
                        <TabPreviewOverflow
                            formData={formData}
                            setFormData={setFormData}
                            dynamicAssets={dynamicAssets}
                            assets={assets}
                            steps={steps}
                            pointsModels={pointsModels}
                            onSubmit={onSummit}
                            programmings={programmings}
                        />
                    </Tab>
                </Tabs>
            </div>
        </>
    );
};

export default FormTemplate;
